// Tiroir « Règles cashless par type d'accès » du chef de pôle : une ligne par type d'accès, une case
// par crédit / unité A-D (valeurs fixes). Enregistré automatiquement (useAutosave). Réservé aux
// formulaires « pôle » dont le contact de config a activé le réglage cashless (config.poleCashless) —
// le serveur vérifie, cet écran ne fait que l'afficher quand c'est autorisé.
import React, { useState } from 'react';
import { Drawer, Box, Stack, Typography, IconButton, CircularProgress, Alert } from '@mui/material';
import { X, Check } from 'lucide-react';
import useAutosave from './useAutosave';
import { CASHLESS_FIELDS, rulesToCells, cellsToRules, hasAdvancedRule } from './poleTeamModel';

const GRID = 'minmax(0, 1fr) repeat(5, 62px)';

const STATUS_VIEW = {
  idle: { label: 'Enregistrement automatique', color: '#5e646a' },
  saving: { label: 'Enregistrement…', color: '#5e646a' },
  saved: { label: 'Enregistré', color: '#007739' },
  error: { label: 'Non enregistré', color: '#b42318' },
};

const cellSx = {
  width: '100%', height: 32, px: 0.75, fontFamily: 'inherit', fontSize: 13, color: '#002F36', bgcolor: '#f8fafb',
  border: '1px solid #e3e8ec', borderRadius: '6px', outline: 'none', boxSizing: 'border-box',
  '&:focus': { borderColor: '#00B797', bgcolor: '#fff', boxShadow: '0 0 0 3px #00b79726' },
};

/** `onSave({ [clé du type]: { credit, units } })` : règles complètes ; rejette en cas d'échec. */
const PoleCashlessDrawer = ({ open, onClose, accessTypes, typeRules, onSave }) => {
  // Seuls les types modifiés ici sont dans le brouillon ; les autres gardent les règles du serveur.
  const [draft, setDraft] = useState({});
  const cellsOf = (key) => draft[key] || rulesToCells(typeRules?.[key]);

  const { status, error } = useAutosave({
    value: draft,
    saved: {},
    save: async (sent) => {
      const merged = {};
      accessTypes.forEach((t) => {
        if (sent[t.key]) merged[t.key] = cellsToRules(sent[t.key]);
        else if (typeRules?.[t.key]) merged[t.key] = typeRules[t.key];
      });
      await onSave(merged);
      // Brouillon vidé seulement s'il n'a pas bougé pendant l'envoi (sinon on perdrait la frappe).
      setDraft((prev) => (JSON.stringify(prev) === JSON.stringify(sent) ? {} : prev));
    },
  });
  const view = STATUS_VIEW[status] || STATUS_VIEW.idle;

  const setCell = (key, field, value) => setDraft((prev) => ({ ...prev, [key]: { ...cellsOf(key), [field]: value } }));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 'min(560px, 100vw)' } }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ p: 2.5, borderBottom: '1px solid #e9edf1' }}>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Règles cashless par type d'accès</Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: '#5e646a' }}>
            Appliquées à chaque personne selon son type. Une valeur personnalisée sur une ligne remplace la règle.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ height: 32, color: view.color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {status === 'saving' && <CircularProgress size={11} color="inherit" />}
          {status === 'saved' && <Check size={13} />}
          <span>{view.label}</span>
        </Stack>
        <IconButton size="small" aria-label="Fermer" onClick={onClose}><X size={18} /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 0.75, px: 2.5, py: 1.25, fontSize: 11, fontWeight: 600, color: '#5e646a', bgcolor: '#f8fafb', borderBottom: '1px solid #e9edf1' }}>
        <span>Type d'accès</span>
        {CASHLESS_FIELDS.map((f) => <span key={f.key}>{f.label}</span>)}
      </Box>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 2.5 }}>
        {accessTypes.length === 0 && (
          <Typography sx={{ py: 3, fontSize: 13, color: '#5e646a' }}>Aucun type d'accès défini pour cet événement.</Typography>
        )}
        {accessTypes.map((t) => {
          const cells = cellsOf(t.key);
          const advanced = !draft[t.key] && hasAdvancedRule(typeRules?.[t.key]);
          return (
            <Box key={t.key} sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 0.75, alignItems: 'center', py: 0.75, borderBottom: '1px solid #f1f4f6' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap title={t.label} sx={{ fontSize: 13, fontWeight: 600 }}>{t.label}</Typography>
                {advanced && (
                  <Typography sx={{ fontSize: 11, color: '#b54708' }}>Règle par jour : une saisie ici la remplace</Typography>
                )}
              </Box>
              {CASHLESS_FIELDS.map((f) => (
                <Box
                  key={f.key} component="input" type="number" min={0} placeholder="0"
                  aria-label={`${f.label} — ${t.label}`}
                  value={cells[f.key]} onChange={(e) => setCell(t.key, f.key, e.target.value)} sx={cellSx}
                />
              ))}
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
};

export default PoleCashlessDrawer;
